import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { createSkillWithAi } from '@/lib/skillCreatorStream';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, Sparkles } from 'lucide-react';
import { useRef, useState } from 'react';

interface ProgressEntry {
  id: string;
  kind: 'progress' | 'iteration' | 'text' | 'tool_start' | 'tool_done';
  toolName?: string;
  message: string;
  status?: 'ok' | 'error';
}

const PLACEHOLDER =
  '예) works DB 의 t_timelog 작업일지를 기반으로 대화하게 해줘. 관리자는 전체, 일반 사용자는 본인 것만 볼 수 있게.';

interface Hint {
  /** 칩에 보이는 짧은 제목. */
  label: string;
  /** 클릭 시 textarea 에 채워질 전체 문구. */
  prompt: string;
}

const HINTS: Hint[] = [
  {
    label: 'DB 작업일지 — 권한별 조회',
    prompt:
      'works DB 의 t_timelog 에 직원들이 작성한 작업일지가 들어 있어. 이걸 기반으로 대화할 수 있게 해줘. 관리자는 모든 데이터를 볼 수 있고, 일반 사용자는 본인 것만, 팀장은 팀원들 것까지 볼 수 있도록 팀 이름으로 권한을 걸어줘.',
  },
  {
    label: '매출 데이터 분석 도우미',
    prompt:
      'sales DB 의 주문/상품 테이블을 분석해서 매출 추이, 베스트셀러, 지역별 실적을 물어보면 답하고 표나 차트로 보여주는 분석 도우미를 만들어줘. 모든 사용자가 전체 데이터를 조회할 수 있게.',
  },
  {
    label: '휴가·근태 규정 안내',
    prompt:
      '인사 규정과 휴가 정책 자료를 바탕으로 직원들이 연차, 반차, 경조사 휴가 같은 걸 물어보면 안내해주는 도우미를 만들어줘.',
  },
  {
    label: '내 엑셀 파일 질문',
    prompt:
      '사용자가 올린 엑셀 파일의 내용을 읽어서 합계·평균을 계산하거나 특정 조건의 행을 찾아주는 식으로 자기 파일에 대해 질문할 수 있게 해줘.',
  },
];

export function AdminSkillCreateWithAi() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [running, setRunning] = useState(false);
  const [entries, setEntries] = useState<ProgressEntry[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [heartbeat, setHeartbeat] = useState<number | null>(null);
  const idCounter = useRef(0);
  /** 현재 누적 중인 text_delta 의 entry id (한 iter 내 같은 entry 에 append). */
  const currentTextEntryId = useRef<string | null>(null);

  const newId = () => {
    idCounter.current += 1;
    return String(idCounter.current);
  };

  const pushEntry = (e: Omit<ProgressEntry, 'id'>) => {
    const id = newId();
    setEntries((prev) => [...prev, { id, ...e }]);
    return id;
  };

  const start = async () => {
    if (running) return;
    const trimmed = prompt.trim();
    if (trimmed.length < 1) {
      setErrorMsg('프롬프트를 입력하세요.');
      return;
    }
    setErrorMsg(null);
    setEntries([]);
    setHeartbeat(null);
    currentTextEntryId.current = null;
    setRunning(true);
    try {
      const result = await createSkillWithAi(trimmed, {
        onProgress: (message) => {
          setHeartbeat(null);
          currentTextEntryId.current = null;
          pushEntry({ kind: 'progress', message });
        },
        onIterationStart: ({ iter, limit }) => {
          setHeartbeat(null);
          currentTextEntryId.current = null;
          pushEntry({ kind: 'iteration', message: `반복 ${iter} / ${limit}` });
        },
        onTextDelta: (text) => {
          setHeartbeat(null);
          // 같은 iter 내 첫 delta 면 새 entry 생성, 이후엔 append.
          const currentId = currentTextEntryId.current;
          if (currentId === null) {
            const id = newId();
            currentTextEntryId.current = id;
            setEntries((prev) => [...prev, { id, kind: 'text', message: text }]);
          } else {
            setEntries((prev) =>
              prev.map((p) => (p.id === currentId ? { ...p, message: p.message + text } : p)),
            );
          }
        },
        onToolCallStart: ({ toolCallId, toolName, argsPreview }) => {
          setHeartbeat(null);
          currentTextEntryId.current = null;
          // 같은 toolCallId 가 두 번 올 수 있음 (adapter 가 start 먼저 보내고, 라우트가 done 시점에 다시 보냄).
          // 두 번째 호출은 args 가 더 충실 → 같은 entry 갱신.
          setEntries((prev) => {
            const existing = prev.find((p) => p.id === toolCallId);
            if (existing) {
              return prev.map((p) =>
                p.id === toolCallId ? { ...p, toolName, message: argsPreview || '(인자 없음)' } : p,
              );
            }
            return [
              ...prev,
              {
                id: toolCallId,
                kind: 'tool_start',
                toolName,
                message: argsPreview || '(인자 없음)',
              },
            ];
          });
        },
        onToolCallDone: ({ toolCallId, status, summary }) => {
          setHeartbeat(null);
          currentTextEntryId.current = null;
          setEntries((prev) =>
            prev.map((p) =>
              p.id === toolCallId ? { ...p, kind: 'tool_done', status, message: summary } : p,
            ),
          );
        },
        onHeartbeat: ({ elapsedSec }) => {
          setHeartbeat(elapsedSec);
        },
      });
      setHeartbeat(null);
      pushEntry({
        kind: 'progress',
        message: `완료 — Skill draft "${result.slug}" 생성됨. 편집 화면으로 이동합니다.`,
      });
      setTimeout(() => {
        navigate({
          to: '/admin/skills/$id',
          params: { id: result.skillId },
          search: { dataSourceId: undefined },
        });
      }, 600);
    } catch (err) {
      setHeartbeat(null);
      setErrorMsg(err instanceof Error ? err.message : '알 수 없는 오류');
      setRunning(false);
    }
  };

  return (
    <PageShell
      header={
        <PageHeader
          title="AI 로 Skill 만들기"
          description="원하는 스킬을 자연어로 설명하면 LLM 이 등록된 데이터소스를 직접 탐색해서 system prompt + 도구 + 권한 컬럼까지 자동으로 채워줍니다. 결과는 비활성 (draft) 상태로 저장되며, 편집 화면에서 검토 후 활성화하세요."
        />
      }
    >
      <div className="rounded-2xl border border-border bg-surface p-5 space-y-4">
        <label className="block">
          <span className="text-sm font-medium">스킬 설명</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={running}
            placeholder={PLACEHOLDER}
            rows={6}
            className="mt-2 w-full rounded-md border border-border bg-surface-muted/30 px-3 py-2.5 text-sm font-mono leading-6 placeholder:text-foreground-faint focus:outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-60"
          />
          <span className="mt-1 block text-xs text-foreground-muted">
            등록된 데이터소스의 테이블 / 컬럼 / 통계를 그대로 활용합니다. 데이터소스가 등록돼 있어야
            정확한 결과가 나옵니다. 첫 응답까지 로컬 LLM 은 30~60초 걸릴 수 있습니다.
          </span>

          <div className="mt-3">
            <div className="mb-1.5 flex items-center gap-1 text-xs font-medium text-foreground-muted">
              <Sparkles className="size-3" />
              예시 — 클릭하면 입력창에 채워집니다
            </div>
            <div className="flex flex-wrap gap-2">
              {HINTS.map((h) => (
                <button
                  key={h.label}
                  type="button"
                  disabled={running}
                  onClick={() => setPrompt(h.prompt)}
                  className="rounded-full border border-border bg-surface-muted/40 px-3 py-1 text-xs text-foreground-muted transition hover:border-accent hover:bg-accent-soft hover:text-foreground disabled:opacity-50"
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        </label>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            type="button"
            disabled={running}
            onClick={() => navigate({ to: '/admin/skills' })}
          >
            취소
          </Button>
          <Button onClick={start} disabled={running}>
            {running ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                생성 중…
              </>
            ) : (
              <>
                <Sparkles className="size-4" />
                스킬 만들기
              </>
            )}
          </Button>
        </div>
      </div>

      {errorMsg ? (
        <div
          role="alert"
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive whitespace-pre-wrap"
        >
          {errorMsg}
        </div>
      ) : null}

      {entries.length > 0 ? (
        <div className="mt-6 rounded-2xl border border-border bg-surface p-5">
          <div className="text-xs uppercase tracking-wider text-foreground-muted mb-3 flex items-center justify-between">
            <span>진행 상황</span>
            {running ? (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 normal-case tracking-normal text-[11px]',
                  heartbeat !== null ? 'text-[hsl(40_95%_55%)]' : 'text-foreground-muted',
                )}
              >
                <Loader2 className="size-3 animate-spin" />
                {heartbeat !== null ? `LLM 응답 대기 중… (${heartbeat}초)` : 'LLM 응답 수신 중…'}
              </span>
            ) : null}
          </div>
          <ul className="space-y-2">
            {entries.map((e) => (
              <li
                key={e.id}
                className={cn(
                  'rounded-md px-3 py-2 text-sm flex items-start gap-2',
                  e.kind === 'progress' && 'bg-surface-muted/50',
                  e.kind === 'iteration' &&
                    'bg-surface-muted/30 text-[11px] uppercase tracking-wider text-foreground-muted',
                  e.kind === 'text' &&
                    'bg-surface-muted/20 font-mono text-xs text-foreground-muted whitespace-pre-wrap',
                  e.kind === 'tool_start' && 'bg-[hsl(210_90%_55%)]/10 text-[hsl(210_90%_55%)]',
                  e.kind === 'tool_done' && e.status === 'ok' && 'bg-success/10 text-success',
                  e.kind === 'tool_done' &&
                    e.status === 'error' &&
                    'bg-destructive/10 text-destructive',
                )}
              >
                {e.kind === 'tool_start' ? (
                  <Loader2 className="size-3.5 mt-0.5 shrink-0 animate-spin" />
                ) : null}
                <div className="min-w-0 flex-1">
                  {e.toolName ? (
                    <span className="font-mono text-[11px] mr-2 opacity-80">{e.toolName}</span>
                  ) : null}
                  <span className="break-words">{e.message}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </PageShell>
  );
}
